import {logger} from '../../../logger';
import React from 'react';
import {action, makeObservable, observable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout, klaviyo_identify} from '../../../api';
import {CheckoutInputElement, checkoutFieldDomIds} from '../inputs/checkoutInputs';
import {addressHasStates, isKnownCountryCode, redesignCountryList, selectLostItsValue, stateOptions, stateValueForCountry} from '../inputs/addressStates';
import {isNullOrWhiteSpace} from '../../../util';
import {ICheckoutStep, ICheckoutType} from '../../i';
import AbStep, {AbStepState} from '../ab-step/ab-redesign-step';
import {AbAddressLines} from '../ab-step/ab-redesign-address-summary';

/**
 * Billing address step of the redesigned checkout (step 4, between Shipping method and Payment). It is part of the step
 * list only while the shopper has unticked "My billing address is the same as my shipping address" on the Shipping
 * address card (or the server stopped on `paymentAddr` regardless); with the box ticked the checkout has four steps and
 * Payment is step 4 (see showsBillingStep).
 *
 * Same server flow as the control's PaymentAddr: 'cc-ship-select' lands on `payment` when
 * `billing.addrSameAsShip` is set and on `paymentAddr` otherwise; "Continue to payment" syncs the
 * "same as shipping" choice ('checkout-sync' copies ship -> billing on the server) or posts the
 * billing form with 'cc-billing-addr-submit', which validates and stays on `paymentAddr` on error.
 */

const addressFields = ['firstName', 'lastName', 'companyName', 'address1', 'address2', 'address3', 'address4', 'city', 'state', 'country', 'postalCode'] as const;

/**
 * Prefills billing from shipping, as the control's "Billing address same as shipping" toggle does. Field errors are
 * cleared too: the server seeds billing from the shipping form's first submit ('cc-ship-addr-submit', when billing is
 * still empty) together with that submit's validation state, which must not show under the freshly copied values.
 */
export function copyShippingToBilling(): void {
    const billing = store.checkout.billing;
    const ship = store.checkout.ship.addr;
    addressFields.forEach(f => {
        billing.addr[f].value = ship[f].value;
        billing.addr[f].valid.isError = false;
        billing.addr[f].valid.msg = '';
    });
    billing.addr.isBusinessAddress = ship.isBusinessAddress;
    store.checkout.messages = [];
    billing.messages = [];

    // The shipping form stores the APO/FPO region as a pseudo company; a card is not billed to "Armed Forces Europe".
    if (billing.addr.companyName.value.startsWith('Armed Forces')) {
        billing.addr.companyName.value = '';
    }
}

// The server cannot tell its default (addrSameAsShip false) from a shopper who unticked the box, and it seeds the
// billing address from the shipping form, so the redesign remembers the shopper's own choice per cart. sessionStorage
// survives a reload of the tab; without it the default simply applies again.
const billingChoiceKey = () => `chc_billing_choice:${store.checkout.cart?.cartId ?? 0}`;
// In-memory twin of the marker, so an untick sticks for this page even when site data is blocked.
let choiceMadeThisPage = false;

export function billingChoiceMade(): boolean {
    if (choiceMadeThisPage) {
        return true;
    }
    try {
        return sessionStorage.getItem(billingChoiceKey()) === '1';
    } catch {
        return false;
    }
}

export function markBillingChoice(): void {
    choiceMadeThisPage = true;
    try {
        sessionStorage.setItem(billingChoiceKey(), '1');
    } catch {
        // storage unavailable
    }
}

/**
 * Redesign default when the Billing address card opens: "Same as shipping address" is pre-ticked (the server's own
 * default is false, which is why 'cc-ship-select' stops on `paymentAddr`). Client-side only: "Continue to payment"
 * carries the flag to the server. A shopper who already decided for this cart is left alone.
 */
export function applyBillingDefault(): void {
    if (store.checkout.billing.addrSameAsShip || billingChoiceMade()) {
        return;
    }
    runInAction(() => {
        store.checkout.billing.addrSameAsShip = true;
        copyShippingToBilling();
    });
}

/**
 * True while the redesign's default stands: a normal (not express) checkout whose shopper has not decided about the
 * billing address for this cart yet. The default is "same as shipping": the shell sets the flag client-side as soon as
 * the cart loads (four steps from the first paint), and the Shipping address card sets and syncs it when it mounts
 * ('checkout-sync', because the server's full-copy replies reset the flag until the server has it).
 */
export function billingDefaultApplies(): boolean {
    return store.checkout.CheckoutType === ICheckoutType.normal && !billingChoiceMade();
}

/** True while the Billing address card is one of the steps: a separate billing address was chosen, or the card is the active step. */
export function showsBillingStep(): boolean {
    return !store.checkout.billing.addrSameAsShip || store.checkout.cstep === ICheckoutStep.paymentAddr;
}

@observer
class BillingForm extends React.Component {
    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.initBillingAddressBook();
    }

    // Mirrors the control's BillingWrite: the first address-book billing entry is loaded when none was picked.
    @action
    initBillingAddressBook = () => {
        const book = store.checkout.AddressBookAddresses;
        if (book != null && book.length > 0 && store.checkout.billing.AddressID < 1) {
            const a = book.filter(x => x.isBilling);
            if (a.length > 0) {
                store.checkout.billing.AddressID = a[0].AddressID;
                store.setBillingAddressToBook();
            }
        }
    };

    @action
    handleChangeAddressBook = (e: React.ChangeEvent<HTMLSelectElement>) => {
        store.checkout.billing.AddressID = parseInt(e.target.value, 10);
        store.setBillingAddressToBook();
    };

    @action
    showMore = async () => {
        store.checkout.billing.addr.addrShowMore = true;
        await checkout('checkout-sync');
    };

    @action
    showMoreCompany = () => {
        store.checkout.billing.addr.addrShowMoreCompany = true;
    };

    fieldId(o: {autocomplete: string}) {
        return checkoutFieldDomIds(o.autocomplete, store.checkout.ab, 'billing').inputId;
    }

    render() {
        const addr = store.checkout.billing.addr;
        const ab = store.checkout.ab;
        const hasStates = addressHasStates(addr);
        // The server keeps `addrShowMore` across round trips but not `addrShowMoreCompany`, and the prefilled
        // shipping values may already carry a line 2 or a company: a filled field always stays visible.
        const showLine2 = addr.addrShowMore || !isNullOrWhiteSpace(addr.address2.value);
        const showCompany = addr.addrShowMoreCompany || !isNullOrWhiteSpace(addr.companyName.value);
        const required = <span className="ab-req">*</span>;

        return (
            <div className="ab-billing-form">
                {store.checkout.AddressBookAddresses != null && store.checkout.AddressBookAddresses.length > 0 && (
                    <div className="ab-fields">
                        <div className="ab-field ab-full">
                            <label htmlFor="billing-address-book">Address book</label>
                            <select
                                id="billing-address-book"
                                className="form-select"
                                name="billing-address-book"
                                autoComplete="off"
                                value={store.checkout.billing.AddressID}
                                aria-label="Billing Address Book"
                                onChange={this.handleChangeAddressBook}
                            >
                                <option value={-1}>Select Address</option>
                                {store.checkout.AddressBookAddresses.map(e => (
                                    <option
                                        value={e.AddressID}
                                        key={e.AddressID}
                                    >
                                        {e.Nickname}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="ab-fields">
                    <div className="ab-field">
                        <label htmlFor={this.fieldId(addr.firstName)}>First name {required}</label>
                        <CheckoutInputElement
                            ab={ab}
                            o={addr.firstName}
                            required={true}
                            billing={true}
                            idSuffix="billing"
                            ariaLabel="Billing First Name"
                            placeholderOverride="Enter"
                        />
                    </div>
                    <div className="ab-field">
                        <label htmlFor={this.fieldId(addr.lastName)}>Last name {required}</label>
                        <CheckoutInputElement
                            ab={ab}
                            o={addr.lastName}
                            required={true}
                            billing={true}
                            idSuffix="billing"
                            ariaLabel="Billing Last Name"
                            placeholderOverride="Enter"
                        />
                    </div>
                </div>

                {showCompany && (
                    <div className="ab-fields">
                        <div className="ab-field ab-full">
                            <label htmlFor={this.fieldId(addr.companyName)}>Company</label>
                            <CheckoutInputElement
                                ab={ab}
                                o={addr.companyName}
                                required={false}
                                billing={true}
                                idSuffix="billing"
                                ariaLabel="Billing Company"
                                placeholderOverride="Enter"
                            />
                        </div>
                    </div>
                )}

                <div className="ab-fields">
                    <div className="ab-field ab-full">
                        <label htmlFor={this.fieldId(addr.address1)}>Address</label>
                        <CheckoutInputElement
                            ab={ab}
                            o={addr.address1}
                            required={true}
                            billing={true}
                            idSuffix="billing"
                            ariaLabel="Billing Address Line 1"
                            placeholderOverride="Address line"
                        />
                        <div className="ab-hint">Include apartment, suite, or unit if applicable</div>
                    </div>
                </div>

                {showLine2 && (
                    <div className="ab-fields ab-fields-line2">
                        <div className="ab-field ab-full">
                            <label htmlFor={this.fieldId(addr.address2)}>Address line 2</label>
                            <CheckoutInputElement
                                ab={ab}
                                o={addr.address2}
                                required={false}
                                billing={true}
                                idSuffix="billing"
                                ariaLabel="Billing Address Line 2"
                                placeholderOverride="Apt, suite, unit, floor, etc."
                            />
                        </div>
                    </div>
                )}

                {(!showLine2 || !showCompany) && (
                    <div className="ab-add-links">
                        {!showLine2 && (
                            <button
                                type="button"
                                className="ab-add-link"
                                onClick={this.showMore}
                                aria-label="Add billing address line 2"
                            >
                                <i
                                    className="fas fa-plus"
                                    aria-hidden={true}
                                />
                                Add address line
                            </button>
                        )}
                        {!showCompany && (
                            <button
                                type="button"
                                className="ab-add-link"
                                onClick={this.showMoreCompany}
                                aria-label="Add billing company"
                            >
                                <i
                                    className="fas fa-plus"
                                    aria-hidden={true}
                                />
                                Add company
                            </button>
                        )}
                    </div>
                )}

                <div className="ab-fields ab-cols-3">
                    <div className="ab-field">
                        <label htmlFor={this.fieldId(addr.city)}>City</label>
                        <CheckoutInputElement
                            ab={ab}
                            o={addr.city}
                            required={true}
                            billing={true}
                            idSuffix="billing"
                            ariaLabel="Billing City"
                            placeholderOverride="Enter"
                        />
                    </div>

                    <div className="ab-field">
                        <label htmlFor={hasStates ? 'billing-states' : 'billing-region'}>State or region</label>
                        {hasStates ? (
                            <select
                                id="billing-states"
                                name="billing-states"
                                className="form-select"
                                value={addr.state.value}
                                onChange={e => {
                                    // An autofilled value the list does not have leaves the select on no option: keep the current one.
                                    if (selectLostItsValue(e.target)) {
                                        e.target.value = addr.state.value;
                                        return;
                                    }
                                    addr.state.value = e.target.value;
                                    addr.state.valid.isError = false;
                                }}
                                autoComplete="billing address-level1"
                                aria-label="Billing State or Region"
                            >
                                {stateOptions(addr.country.value)}
                            </select>
                        ) : (
                            <input
                                id="billing-region"
                                name="billing-region"
                                className="form-control"
                                value={addr.state.value}
                                onChange={e => (addr.state.value = e.target.value)}
                                placeholder="Enter"
                                type="text"
                                autoComplete="billing address-level1"
                                aria-label="Billing State or Region"
                            />
                        )}
                        {addr.state.valid.isError && <div className="input-error">{addr.state.valid.msg}</div>}
                    </div>

                    <div className="ab-field">
                        <label htmlFor={this.fieldId(addr.postalCode)}>Postal code</label>
                        <CheckoutInputElement
                            ab={ab}
                            o={addr.postalCode}
                            type="text"
                            required={true}
                            hideSpinner={true}
                            billing={true}
                            idSuffix="billing"
                            ariaLabel="Billing Postal Code"
                            placeholderOverride="Enter"
                        />
                    </div>
                </div>

                <div className="ab-fields">
                    <div className="ab-field ab-full">
                        <label htmlFor="billing-country">Country</label>
                        <select
                            id="billing-country"
                            className="form-select"
                            name="billing-country"
                            autoComplete="billing country"
                            value={addr.country.value}
                            aria-label="Billing Country"
                            onChange={e => {
                                // As on the shipping form: an autofill that matches no option must not land on the first country.
                                if (selectLostItsValue(e.target) || !isKnownCountryCode(e.target.value)) {
                                    e.target.value = addr.country.value;
                                    return;
                                }
                                addr.country.value = e.target.value;
                                addr.state.value = stateValueForCountry(e.target.value, addr.state.value);
                                addr.state.valid.isError = false;
                            }}
                        >
                            {redesignCountryList().map(e => (
                                <option
                                    value={e.code}
                                    key={e.id}
                                >
                                    {e.name}
                                </option>
                            ))}
                        </select>
                        {addr.country.valid.isError && <div className="input-error">{addr.country.valid.msg}</div>}
                    </div>
                </div>
            </div>
        );
    }
}

@observer
export class BillingStep extends React.Component {
    @observable loading: boolean = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    get stepState(): AbStepState {
        const step = store.checkout.cstep;
        if (step === ICheckoutStep.paymentAddr) {
            return 'active';
        }
        return step === ICheckoutStep.payment || step === ICheckoutStep.success ? 'done' : 'pending';
    }

    componentDidMount() {
        this.applyDefault();
    }

    componentDidUpdate() {
        this.applyDefault();
    }

    // The card is an explicit stop: it opens with the box pre-ticked and the shipping address shown, and the shopper confirms.
    applyDefault = () => {
        if (this.stepState === 'active') {
            applyBillingDefault();
        }
    };

    // Both directions prefill billing from shipping, like the control's toggle; the card stays open until "Continue to payment".
    @action
    toggleSameAsShip = async () => {
        markBillingChoice();
        store.checkout.billing.addrSameAsShip = !store.checkout.billing.addrSameAsShip;
        copyShippingToBilling();
        await checkout('checkout-sync');
    };

    @action
    submit = async () => {
        const billing = store.checkout.billing;
        if (!billing.addrSameAsShip && billing.addr.state.value === 'AL1') {
            billing.addr.state.valid.isError = true;
            billing.addr.state.valid.msg = 'Please select a state/region';
            return;
        }

        this.loading = true;
        markBillingChoice();
        try {
            if (billing.addrSameAsShip) {
                // 'checkout-sync' with cstep = payment: the server copies the flag and ship -> billing in the same call.
                await store.moveStep(ICheckoutStep.payment);
            } else {
                await checkout('cc-billing-addr-submit');
                try {
                    await klaviyo_identify();
                } catch (err) {
                    logger.error(err);
                }
            }
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    render() {
        if (!showsBillingStep()) {
            return null;
        }
        const state = this.stepState;
        const billing = store.checkout.billing;
        const canEdit = !store.uiCheckout.processing;

        return (
            <div id="c-billing">
                <AbStep
                    className="ab-billing"
                    num={4}
                    title="Billing address"
                    state={state}
                    onEdit={
                        canEdit
                            ? () => {
                                  // Editing is a decision: a server-filled billing address (PayPal Express payer) must not be re-ticked away.
                                  markBillingChoice();
                                  store.moveStep(ICheckoutStep.paymentAddr);
                              }
                            : undefined
                    }
                    editLabel="Edit billing address"
                >
                    {state === 'active' && (
                        <div className="ab-step-body">
                            <label className="ab-optin ab-billing-same">
                                <input
                                    type="checkbox"
                                    name="billing-address-same-as-ship"
                                    aria-label="Billing address same as shipping address"
                                    checked={billing.addrSameAsShip}
                                    onChange={this.toggleSameAsShip}
                                />
                                <span>Same as shipping address</span>
                            </label>
                            <div className="ab-hint ab-billing-hint">For your protection, this must match what appears on your credit card or bank statement</div>

                            {billing.addrSameAsShip && (
                                <div
                                    className="ab-billing-preview"
                                    role="group"
                                    aria-label="Shipping address used for billing"
                                >
                                    <AbAddressLines addr={store.checkout.ship.addr} />
                                </div>
                            )}
                            {!billing.addrSameAsShip && <BillingForm />}

                            {billing.messages.map((e, i) => (
                                <div
                                    className="alert alert-danger"
                                    key={i.toString()}
                                    role="alert"
                                >
                                    <span style={{paddingRight: 6}}>
                                        <i
                                            className="fas fa-exclamation-square"
                                            aria-hidden={true}
                                        ></i>
                                    </span>{' '}
                                    {e.msg}
                                </div>
                            ))}

                            <div className="ab-step-actions">
                                <div
                                    aria-live="assertive"
                                    aria-atomic="true"
                                    className="sr-only"
                                >
                                    {this.loading ? 'Saving billing address' : ''}
                                </div>
                                <button
                                    type="button"
                                    className="btn-checkout btn-checkout-yellow"
                                    onClick={this.submit}
                                    disabled={this.loading}
                                    aria-busy={this.loading}
                                    aria-label="Continue to payment"
                                >
                                    {this.loading ? 'Saving' : 'Continue to payment'}
                                    {this.loading && (
                                        <span className="pad-left">
                                            <i
                                                className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                                                aria-hidden={true}
                                            ></i>
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                    {state === 'done' && (
                        <div className="ab-step-summary ab-billing-summary">
                            {billing.addrSameAsShip && <div className="ab-billing-same-note">Same as shipping address</div>}
                            <AbAddressLines addr={billing.addrSameAsShip ? store.checkout.ship.addr : billing.addr} />
                        </div>
                    )}
                </AbStep>
            </div>
        );
    }
}

export default BillingStep;
